import React, { Fragment, useEffect, useState } from 'react';
import { Button, Form, InputGroup } from 'react-bootstrap';
import moment from 'moment';
import PropTypes from 'prop-types';

import { requestDownload } from 'api/sfms/mix/ratio';
import MonthPicker from 'components/common-sfims/form/MonthPicker';
import Select from 'components/common-sfims/form/Select';
import Modal from 'components/common-sfims/modal/Common';
import { array2ValueAndText } from 'function/convert';
import { onInputChange } from 'function/event';
import { downloadFile } from 'function/file';
import { getText } from 'function/language';
import { getLoginInform } from 'function/login';
import { getErrorMessage } from 'function/message';
import { isValidRequired } from 'function/valid';
import { useConfirmModalState } from 'global/confirmModalState';
import usePlantState from 'global/plantState';
import useObjReducer from 'hooks/useObjReducer';
import { RESPONSE_CODE } from 'types/const';

/**
 * 배합비 등록 관리 > 배합비 - SAP용 다운로드
 */
const Download = ({ show, onHide, onDownload }) => {
  /* variable */
  const { fatoryCodeSeq } = getLoginInform();

  /* recoil */
  const { setConfirmModal } = useConfirmModalState();
  const {
    plant: { hasReads: hasReadPlants }
  } = usePlantState();

  /* state */
  // 다운로드 비활성화 여부
  const [isDisableDownload, setIsDisableDownload] = useState(false);
  // 배합비 SAP용 다운로드 요청 데이터
  const [downloadData, setDownloadData] = useObjReducer({});

  /* watch */
  useEffect(() => {
    // 모달이 표시된 경우 데이터 초기화
    if (show) {
      setDownloadData({
        factoryCodeSeq: fatoryCodeSeq,
        mixYm: moment()
          .add(moment().date() >= 22 ? 1 : 0, 'months')
          .format('YYYYMM')
      });
    }
  }, [show]);

  /* event */
  // 다운로드
  const onSubmit = async () => {
    setIsDisableDownload(true);

    try {
      // 필수 항목 입력 유효성 확인
      if (!isValidRequired(downloadData)) {
        throw { code: RESPONSE_CODE.BadRequestSearch };
      }

      const { data, name } = await requestDownload(downloadData);

      downloadFile(data, name);

      // 다운로드 시 이벤트 실행
      onDownload();

      // 모달 숨김
      onHide();
    } catch (error) {
      console.error(error);
      setConfirmModal({
        isOpen: true,
        message: getErrorMessage(error?.code, error?.error)
      });
    } finally {
      setIsDisableDownload(false);
    }
  };

  /* view */
  return (
    <Modal
      show={show}
      className={{ footer: 'align-items-center justify-content-center' }}
      onHide={onHide}
    >
      <Fragment key="header">{getText('download')}</Fragment>
      <Fragment key="body">
        <Form.Group>
          <InputGroup className="mb-3">
            <InputGroup.Text className="w-25 fs--1">
              * {getText('selectFactory')}
            </InputGroup.Text>
            <Select
              datas={array2ValueAndText(hasReadPlants, 'seq', 'pltCode')}
              placeholder="selectFactory"
              value={downloadData.factoryCodeSeq}
              name="factoryCodeSeq"
              onChange={event => onInputChange(event, setDownloadData)}
            />
          </InputGroup>
          <InputGroup>
            <InputGroup.Text className="w-25 fs--1">
              * {getText('selectYm')}
            </InputGroup.Text>
            <MonthPicker
              value={downloadData.mixYm}
              placeholderText={getText('selectYm')}
              wrapperClassName="w-75"
              onUpdate={mixYm => setDownloadData({ mixYm })}
            />
          </InputGroup>
        </Form.Group>
      </Fragment>
      <Fragment key="footer">
        <Button variant="secondary" onClick={onHide}>
          {getText('cancel')}
        </Button>
        <Button
          disabled={isDisableDownload}
          className="ms-2"
          onClick={onSubmit}
        >
          {getText('ok')}
        </Button>
      </Fragment>
    </Modal>
  );
};

Download.propTypes = {
  // 표시 여부
  show: PropTypes.bool,

  // 모달을 숨긴 경우 이벤트
  onHide: PropTypes.func,

  // 다운로드 시 이벤트
  onDownload: PropTypes.func
};
Download.defaultProps = { show: false };

export default Download;
